import {ExternalFile} from 'notebook/util/NoteUtilConstants';
import {ChemicalStructureSearchOperator, Operator, ScoresById} from 'search/SearchConstants';

export enum ChemDrawStoichiometryResult {
  NO_TABLE = 'NO_TABLE',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

interface ChemDrawNoTableStoichiometryData {
  result: ChemDrawStoichiometryResult.NO_TABLE;
}
interface ChemDrawSuccessStoichiometryData {
  result: ChemDrawStoichiometryResult.SUCCESS;
  reactantsHeader: Array<string>;
  reactantsTable: Array<Array<string>>;
  productsHeader: Array<string>;
  productsTable: Array<Array<string>>;
}
interface ChemDrawErrorStoichiometryData {
  result: ChemDrawStoichiometryResult.ERROR;
  message: string;
}

export type ChemDrawStoichiometryData =
  | ChemDrawNoTableStoichiometryData
  | ChemDrawSuccessStoichiometryData
  | ChemDrawErrorStoichiometryData;

// Keep in sync with class definition in benchling/chemistry/models/chemical_structure.py
export enum ChemicalFormatType {
  SMILES = 'smiles',
  MOLFILE = 'molfile',
}

export interface ChemicalStructureInput {
  structureFormat: ChemicalFormatType;
  value: string;
}

export type ChemicalStructureBlob = Blob & {name?: string};

export const MOLECULE_ROW_HEIGHT = 200;

export const EMPTY_MOLFILE =
  '\n  MJ211100                      \n\n  0  0  0  0  0  0  0  0  0  0999 V2000\nM  END\n';

export const EMPTY_MRV = '<cml><MDocument></MDocument></cml>';

export const ChemistryPreferences = {
  SHOULD_HIGHLIGHT_SUBSTRUCTURE: 'shouldHighlightSubstructurePref',
  MIN_SIMILARITY: 'minSimilarityPref',
  CHEM_SEARCH_TYPE: 'chemSearchTypePref',
};

export enum ChemicalProperty {
  ATOM_COUNT = 'ATOM_COUNT',
  EXACT_MOLECULAR_WEIGHT = 'EXACT_MOLECULAR_WEIGHT',
  FRAGMENT_COUNT = 'FRAGMENT_COUNT',
  HEAVY_ATOM_COUNT = 'HEAVY_ATOM_COUNT',
  HYDROGEN_ACCEPTOR_COUNT = 'HYDROGEN_ACCEPTOR_COUNT',
  HYDROGEN_DONOR_COUNT = 'HYDROGEN_DONOR_COUNT',
  LOGP = 'LOGP',
  MOLAR_REFRACTIVITY = 'MOLAR_REFRACTIVITY',
  MOLECULAR_WEIGHT = 'MOLECULAR_WEIGHT',
  NUM_AROMATIC_RINGS = 'NUM_AROMATIC_RINGS',
  RING_COUNT = 'RING_COUNT',
  ROTATABLE_BOND_COUNT = 'ROTATABLE_BOND_COUNT',
  SP3_ATOM_COUNT = 'SP3_ATOM_COUNT',
  TOPOLOGICAL_POLAR_SURFACE_AREA = 'TOPOLOGICAL_POLAR_SURFACE_AREA',
}

export const CHEMICAL_PROPERTY_TO_DISPLAY_NAMES: {[key in ChemicalProperty]: string} = {
  [ChemicalProperty.MOLECULAR_WEIGHT]: 'MW',
  [ChemicalProperty.EXACT_MOLECULAR_WEIGHT]: 'Exact MW',
  [ChemicalProperty.ATOM_COUNT]: 'Atom count',
  [ChemicalProperty.HEAVY_ATOM_COUNT]: 'Heavy atom count',
  [ChemicalProperty.SP3_ATOM_COUNT]: 'sp3 atom count',
  [ChemicalProperty.HYDROGEN_ACCEPTOR_COUNT]: 'HBA',
  [ChemicalProperty.HYDROGEN_DONOR_COUNT]: 'HBD',
  [ChemicalProperty.RING_COUNT]: 'Ring count',
  [ChemicalProperty.NUM_AROMATIC_RINGS]: 'Aromatic ring count',
  [ChemicalProperty.ROTATABLE_BOND_COUNT]: 'Rotatable bond count',
  [ChemicalProperty.LOGP]: 'cLogP',
  [ChemicalProperty.TOPOLOGICAL_POLAR_SURFACE_AREA]: 'TPSA',
  [ChemicalProperty.MOLAR_REFRACTIVITY]: 'Molar refractivity',
  [ChemicalProperty.FRAGMENT_COUNT]: 'Fragment count',
};

export interface ChemSearchFolderItemListingExtras {
  substructureQuery?: string | null;
  shouldHighlightSubstructure?: boolean;
  searchScoresById?: ScoresById;
}

export const SIMILARITY_SCORE_CUTOFFS = [0.99, 0.95, 0.9, 0.8, 0.7, 0.5];

export const CHEM_SEARCH_OPERATOR_TO_DISPLAY_NAME: {[key in ChemicalStructureSearchOperator]: string} = {
  [Operator.SIMILARITY]: 'Similarity',
  [Operator.SUBSTRUCTURE]: 'Substructure',
};

export const DEFAULT_MIN_SIMILARITY_CUTOFF = 0.8;

export const MOLECULE_EXTENSIONS = [
  /*
   * SDF files not supported since multiple molecules could exist in a file.
   * We do support exporting to SDF by adding to the chemicalFileExtensions array in
   * client/notebook/notes/attachments/molecules/DownloadChemicalModal.tsx
   */
  'inchi',
  'mol',
  'smiles',
];

export const REACTION_EXTENSIONS = [
  // 'cdxml' not well supported as an export format
  'cdx',
  'mrv',
  'rdf',
];

export const CHEMICAL_EXTENSIONS = [...MOLECULE_EXTENSIONS, ...REACTION_EXTENSIONS];

// Keep in sync with ReactionFormat in services/chemistry/openapi.yaml
export enum ReactionFormat {
  RXN_V3 = 'rxn:V3',
}

export interface ParseReactionComponentsResponse {
  externalFile: ExternalFile;
  reactants: Array<{moleculeId: string; name: string}>;
  products: Array<{moleculeId: string; name: string}>;
}

export enum MoleculeExportFormat {
  SMILES = 'smiles',
  MOLFILE = 'mol',
  SDF = 'sdf',
}

// This mapping is used by both MoleculeExportFormat and CHEMICAL_EXTENSIONS values, so make sure they stay in sync.
export const EXPORT_DISPLAY_NAME: {[extension: string]: string} = {
  mrv: 'MRV (.mrv)',
  smiles: 'SMILES (.smiles)',
  mol: 'Molfile (.mol)',
  sdf: 'SDF (.sdf)',
  inchi: 'InChI (.inchi)',
  cdx: 'CDX (.cdx)',
  rdf: 'RDF (.rdf)',
  cdxml: 'CDXML (.cdxml)',
};
